import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from 'react';
import pb, { customApi } from '../../pocketbase';
import './DailyCalendar.css';

// Получаем "эффективную" дату (с учётом того, что до 3 утра считается предыдущий день)
const getEffectiveDate = (date = new Date()) => {
    const effectiveDate = new Date(date);
    if (effectiveDate.getHours() < 3) {
        effectiveDate.setDate(effectiveDate.getDate() - 1);
    }
    return effectiveDate;
};

const DailyCalendar = forwardRef(({ onBottomSheetClick, onDayClick, selectedDate }, ref) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [calendarData, setCalendarData] = useState({ opened_indices: [], noted_indices: [] });

    // Смещение для навигации: для недели - количество недель, для месяца - количество месяцев
    const [weekOffset, setWeekOffset] = useState(0);
    const [monthOffset, setMonthOffset] = useState(0);

    // Для анимации свайпа
    const [swipeDirection, setSwipeDirection] = useState(null); // 'left' | 'right' | null
    const [isAnimating, setIsAnimating] = useState(false);

    // Флаг для отслеживания перехода неделя -> месяц (для анимации раскрытия)
    const [justExpanded, setJustExpanded] = useState(false);

    // Для обработки свайпов
    const touchStartX = useRef(null);
    const touchEndX = useRef(null);
    const minSwipeDistance = 50;

    // Получаем эффективную текущую дату (до 3 утра = вчера)
    const effectiveToday = getEffectiveDate();
    const currentYear = effectiveToday.getFullYear();
    const currentMonth = effectiveToday.getMonth();
    const currentDate = effectiveToday.getDate();

    // Функция загрузки данных календаря (вынесена для переиспользования)
    const fetchCalendarData = async () => {
        try {
            // Проверяем сессию
            if (!pb.authStore.isValid) {
                console.log('DailyCalendar: No active session');
                return;
            }

            const data = await customApi('GET', '/api/custom/daily-calendar');

            if (data) {
                console.log('Calendar data received:', data);

                // Transform openings to indices
                const openedIndices = (data.openings || []).map(o => {
                    const date = new Date(o.date);
                    return date.getDate();
                });

                // Transform noted_dates to day indices (for current month)
                const currentMonth = new Date().toISOString().slice(0, 7); // "2024-12"
                const notedIndices = (data.noted_dates || [])
                    .filter(d => d.startsWith(currentMonth)) // Only current month
                    .map(d => new Date(d).getDate());

                setCalendarData({
                    opened_indices: openedIndices,
                    noted_indices: notedIndices
                });
            }
        } catch (e) {
            console.error('Unexpected error fetching calendar data:', e);
        }
    };

    // Предоставляем функции через ref
    useImperativeHandle(ref, () => ({
        refreshCalendarData: fetchCalendarData,
        toggleView: () => {
            // Если открываем месяц - устанавливаем флаг для анимации раскрытия
            if (!isExpanded) {
                setJustExpanded(true);
                setTimeout(() => setJustExpanded(false), 400);
            }
            setIsExpanded(prev => !prev);
        }
    }));

    // Загружаем данные календаря при монтировании
    useEffect(() => {
        fetchCalendarData();
    }, []);

    // Названия дней недели (короткие)
    const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

    // Названия месяцев
    const monthNames = [
        'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
        'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
    ];

    // Вычисляем дату с учетом смещения месяца
    const getViewDate = useCallback(() => {
        const viewDate = new Date(currentYear, currentMonth + monthOffset, 1);
        return viewDate;
    }, [currentYear, currentMonth, monthOffset]);

    const viewDate = getViewDate();
    const viewYear = viewDate.getFullYear();
    const viewMonth = viewDate.getMonth();

    // Получаем первый день месяца для отображаемого месяца
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
    // День недели первого дня (0 = воскресенье, 1 = понедельник, ...)
    let firstDayWeekDay = firstDayOfMonth.getDay();
    // Конвертируем в формат понедельник = 0
    firstDayWeekDay = firstDayWeekDay === 0 ? 6 : firstDayWeekDay - 1;

    // Получаем количество дней в отображаемом месяце
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

    // Получаем даты недели с учетом смещения
    const getWeekDates = useCallback(() => {
        const currentDayOfWeek = effectiveToday.getDay();
        const mondayOffset = currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek;

        // Добавляем смещение недели (7 дней на каждую неделю)
        const totalDayOffset = mondayOffset + (weekOffset * 7);

        const weekDates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(effectiveToday);
            date.setDate(effectiveToday.getDate() + totalDayOffset + i);
            weekDates.push({
                day: date.getDate(),
                month: date.getMonth(),
                year: date.getFullYear(),
                isCurrentMonth: date.getMonth() === currentMonth && date.getFullYear() === currentYear
            });
        }
        return weekDates;
    }, [effectiveToday, weekOffset, currentMonth, currentYear]);

    // Генерируем все дни месяца для месячного вида
    const getAllDaysInMonth = useCallback(() => {
        const days = [];

        // Добавляем пустые ячейки для выравнивания
        for (let i = 0; i < firstDayWeekDay; i++) {
            days.push(null);
        }

        // Добавляем дни месяца
        for (let day = 1; day <= daysInMonth; day++) {
            days.push({
                day,
                month: viewMonth,
                year: viewYear,
                isCurrentMonth: viewMonth === currentMonth && viewYear === currentYear
            });
        }

        return days;
    }, [firstDayWeekDay, daysInMonth, viewMonth, viewYear, currentMonth, currentYear]);

    const weekDates = getWeekDates();
    const allDays = getAllDaysInMonth();

    // Обработчик клика по дню
    const handleDayClick = useCallback((dateInfo, e) => {
        if (!dateInfo || !onDayClick) return;

        e.stopPropagation(); // Предотвращаем срабатывание handleCalendarClick

        const { day, month, year } = dateInfo;
        const clickedDate = new Date(year, month, day);
        const todayStart = new Date(currentYear, currentMonth, currentDate);
        todayStart.setHours(0, 0, 0, 0);
        clickedDate.setHours(0, 0, 0, 0);

        // Разрешаем клик только на прошлые дни и сегодня
        if (clickedDate <= todayStart) {
            onDayClick(new Date(year, month, day));
        }
    }, [onDayClick, currentYear, currentMonth, currentDate]);

    // Обработчики свайпов
    const handleTouchStart = (e) => {
        if (isAnimating) return;
        touchStartX.current = e.touches[0].clientX;
        touchEndX.current = null;
    };

    const handleTouchMove = (e) => {
        if (isAnimating) return;
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (isAnimating) return;
        if (!touchStartX.current || !touchEndX.current) return;

        const distance = touchStartX.current - touchEndX.current;
        const isSwipe = Math.abs(distance) > minSwipeDistance;

        if (isSwipe) {
            const direction = distance > 0 ? 'left' : 'right';
            setSwipeDirection(direction);
            setIsAnimating(true);

            // После анимации выхода меняем данные
            setTimeout(() => {
                if (direction === 'left') {
                    // Свайп влево - следующий период
                    if (isExpanded) {
                        setMonthOffset(prev => prev + 1);
                    } else {
                        setWeekOffset(prev => prev + 1);
                    }
                } else {
                    // Свайп вправо - предыдущий период
                    if (isExpanded) {
                        setMonthOffset(prev => prev - 1);
                    } else {
                        setWeekOffset(prev => prev - 1);
                    }
                }

                // Меняем направление для анимации входа
                setSwipeDirection(direction === 'left' ? 'enter-right' : 'enter-left');

                // Завершаем анимацию
                setTimeout(() => {
                    setSwipeDirection(null);
                    setIsAnimating(false);
                }, 200);
            }, 150);
        }

        touchStartX.current = null;
        touchEndX.current = null;
    };



    // Синхронизация смещений при переключении вида
    useEffect(() => {
        // При переключении вида всегда фокусируемся на активном дне (или эффективном сегодня)
        // Это гарантирует, что активный день будет виден
        const targetDate = selectedDate || effectiveToday;
        const targetDateObj = new Date(targetDate);
        targetDateObj.setHours(0, 0, 0, 0);

        const baseDate = new Date(effectiveToday);
        baseDate.setHours(0, 0, 0, 0);

        // Обновляем смещение месяца
        const mOffset = (targetDateObj.getFullYear() - baseDate.getFullYear()) * 12 + (targetDateObj.getMonth() - baseDate.getMonth());
        setMonthOffset(mOffset);

        // Обновляем смещение недели
        const getMonday = (d) => {
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1);
            const monday = new Date(d);
            monday.setDate(diff);
            monday.setHours(0, 0, 0, 0);
            return monday;
        };

        const targetMonday = getMonday(new Date(targetDateObj));
        const baseMonday = getMonday(new Date(baseDate));

        const diffTime = targetMonday.getTime() - baseMonday.getTime();
        const diffWeeks = Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));

        setWeekOffset(diffWeeks);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded]);

    // Синхронизация календаря с выбранной датой
    useEffect(() => {
        if (!selectedDate) {
            // Если дата сброшена (или null при старте), возвращаемся к эффективному сегодня
            setMonthOffset(0);
            setWeekOffset(0);
            return;
        }

        const targetDate = new Date(selectedDate);
        targetDate.setHours(0, 0, 0, 0);

        const baseDate = new Date(effectiveToday);
        baseDate.setHours(0, 0, 0, 0);

        // Расчет смещения месяца
        const mOffset = (targetDate.getFullYear() - baseDate.getFullYear()) * 12 + (targetDate.getMonth() - baseDate.getMonth());
        setMonthOffset(mOffset);

        // Расчет смещения недели
        // Находим понедельники
        const getMonday = (d) => {
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
            const monday = new Date(d.setDate(diff));
            monday.setHours(0, 0, 0, 0);
            return monday;
        };

        const targetMonday = getMonday(new Date(targetDate));
        const baseMonday = getMonday(new Date(baseDate));

        const diffTime = targetMonday.getTime() - baseMonday.getTime();
        const diffWeeks = Math.round(diffTime / (1000 * 60 * 60 * 24 * 7));

        setWeekOffset(diffWeeks);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    // Слушаем клики по home-bottom-sheet
    useEffect(() => {
        if (isExpanded && onBottomSheetClick) {
            const handleClick = () => {
                setIsExpanded(false);
            };

            const bottomSheet = document.querySelector('.home-bottom-sheet');
            if (bottomSheet) {
                bottomSheet.addEventListener('click', handleClick);
                return () => bottomSheet.removeEventListener('click', handleClick);
            }
        }
    }, [isExpanded, onBottomSheetClick]);

    // Получаем класс анимации
    const getAnimationClass = () => {
        if (!swipeDirection) return '';
        switch (swipeDirection) {
            case 'left': return 'swipe-exit-left';
            case 'right': return 'swipe-exit-right';
            case 'enter-left': return 'swipe-enter-left';
            case 'enter-right': return 'swipe-enter-right';
            default: return '';
        }
    };

    // Функция рендера кружочка с датой
    const renderDateCircle = (dateInfo, idx, rowIndex = null) => {
        if (!dateInfo) {
            const style = rowIndex !== null ? { '--row-index': rowIndex } : {};
            return <div key={idx} className={`calendar-date-circle empty ${rowIndex !== null ? 'cascade-item' : ''}`} style={style}></div>;
        }

        const { day, month, year, isCurrentMonth } = dateInfo;

        // Эффективный сегодняшний день (до 3 утра = вчера)
        const isTodayReal = day === currentDate && month === currentMonth && year === currentYear;

        // Проверяем, что день в прошлом или сегодня (для текущего месяца)
        const dateToCheck = new Date(year, month, day);
        const todayDate = new Date(currentYear, currentMonth, currentDate);
        const isPastOrToday = dateToCheck <= todayDate;

        const hasNote = isCurrentMonth && calendarData.noted_indices?.includes(day);
        // "Если вопрос n-числа был открыт, то все n-числа теперь будут показывать календарное число"
        // Поэтому для isOpened убираем проверку isCurrentMonth
        const isOpened = calendarData.opened_indices?.includes(day);

        // Определяем, выбран ли этот день
        let isActive = false;
        if (selectedDate) {
            isActive = day === selectedDate.getDate() && month === selectedDate.getMonth() && year === selectedDate.getFullYear();
        } else {
            // По умолчанию активен "сегодня"
            isActive = isTodayReal;
        }

        let content = day;
        let className = 'calendar-date-circle';

        // Добавляем класс для каскадной анимации
        if (rowIndex !== null) {
            className += ' cascade-item';
        }

        // Добавляем класс для дней не из текущего месяца
        if (!isCurrentMonth) {
            className += ' other-month';
        }

        // Добавляем класс для кликабельных дней
        if (isPastOrToday && onDayClick) {
            className += ' clickable';
        }

        // Логика активного класса
        if (isActive) {
            className += ' active';
        }

        // Логика маркера для "сегодня", если он не активен
        if (isTodayReal && !isActive) {
            className += ' today-marker';
        }

        // Логика отображения контента для прошлого и сегодня
        if (isPastOrToday) {
            if (hasNote) {
                // Если есть заметка - иконка заметки (даже для активного дня)
                content = <img src={isActive ? "/icons/note-white.svg" : "/icons/note-black.svg"} alt="Note" />;
                className += ' has-note';
            } else if (!isOpened) {
                // Если нет заметки и вопрос НЕ открыт - иконка закрытого вопроса
                content = <img src="/icons/unopened-question.svg" alt="Unopened" />;
                className += ' unopened';
            }
            // Иначе (isOpened && !hasNote) - оставляем число (content = day)
        } else if (isOpened) {
            // Открытые вопросы (без заметки)
            if (isActive) {
                // В активном состоянии показываем галочку
                content = <img src="/icons/checkmark.svg" alt="Check" />;
            } else {
                // В неактивном - просто число
                content = day;
            }
        } else {
            // Неоткрытые вопросы
            content = <img src="/icons/unopened-question.svg" alt="Locked" />;
        }

        // Добавляем класс active к контенту, если это "сегодня" и был открыт "галочкой" в старой логике?
        // Нет, сейчас isActive означает "выбран".

        // Если вопрос открыт и сегодня -> в старом коде была галочка.
        // Сейчас: если сегодня выбран -> isActive -> будет галочка.
        // Если сегодня НЕ выбран (смотрим прошлое) -> !isActive -> will be day number.
        // Но сегодня обычно открыт (или нет).
        // Если сегодня НЕ открыт -> unopened locked icon.

        const style = rowIndex !== null ? { '--row-index': rowIndex } : {};

        return (
            <div
                key={idx}
                className={className}
                style={style}
                onClick={(e) => handleDayClick(dateInfo, e)}
            >
                {content}
            </div>
        );
    };

    // Получаем заголовок для текущего вида
    const getViewTitle = () => {
        if (isExpanded) {
            return `${monthNames[viewMonth]} ${viewYear}`;
        } else {
            // Для недельного вида показываем диапазон дат
            const firstDay = weekDates[0];
            const lastDay = weekDates[6];
            if (firstDay.month === lastDay.month) {
                return `${firstDay.day}–${lastDay.day} ${monthNames[firstDay.month]}`;
            } else {
                return `${firstDay.day} ${monthNames[firstDay.month].slice(0, 3)} – ${lastDay.day} ${monthNames[lastDay.month].slice(0, 3)}`;
            }
        }
    };

    return (
        <div
            className={`daily-calendar ${isExpanded ? 'expanded' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div className="calendar-content">
                {/* Названия дней недели */}
                <div className="calendar-week-header">
                    {weekDays.map((day, idx) => (
                        <div key={idx} className="calendar-day-name">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Недельный вид */}
                {!isExpanded && (
                    <div className={`calendar-week-view ${getAnimationClass()}`}>
                        {weekDates.map((dateInfo, idx) => renderDateCircle(dateInfo, idx))}
                    </div>
                )}

                {/* Месячный вид */}
                {isExpanded && (
                    <div className={`calendar-month-view ${justExpanded ? 'expand-animation' : ''} ${getAnimationClass()}`}>
                        {allDays.map((dateInfo, idx) => {
                            const rowIndex = Math.floor(idx / 7); // Номер строки (0-5)
                            return renderDateCircle(dateInfo, idx, justExpanded ? rowIndex : null);
                        })}
                    </div>
                )}

                {/* Заголовок с текущим периодом (внизу) */}
                {(weekOffset !== 0 || monthOffset !== 0) && (
                    <div
                        className="calendar-period-title"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onDayClick) onDayClick(new Date());
                        }}
                        style={{ cursor: 'pointer' }}
                    >
                        {getViewTitle()} (сбросить)
                    </div>
                )}
            </div>
        </div>
    );
});

export default DailyCalendar;

